import React, { useEffect, useRef } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../auth/context/AuthContext';
import { ROUTES } from '../../../shared/config/routes';
import { getAvatarEmoji } from '../../users/utils/avatarCatalog';

const menuItems = [
  { to: ROUTES.adminCustomers, label: 'Clientes', roles: ['ADMIN', 'STAFF'] },
  { to: ROUTES.adminVoucherList, label: 'Bonos', roles: ['ADMIN', 'STAFF'] },
  { to: ROUTES.adminVoucherCreate, label: 'Crear bono', roles: ['ADMIN', 'STAFF'] },
  { to: ROUTES.adminVoucherRedeem, label: 'Canjear bono', roles: ['ADMIN', 'STAFF'] },
];

export default function AdminShell() {
  const { role, user } = useAuth();
  const location = useLocation();
  const navRef = useRef(null);
  const contentRef = useRef(null);
  const visibleMenuItems = menuItems.filter((item) => item.roles.includes(role));

  useEffect(() => {
    const activeLink = navRef.current?.querySelector('[aria-current="page"]');
    activeLink?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest',
    });

    if (window.innerWidth >= 1280 || !contentRef.current) {
      return;
    }

    const top = Math.max(contentRef.current.getBoundingClientRect().top + window.scrollY - 96, 0);
    window.scrollTo({ top, behavior: 'smooth' });
  }, [location.pathname]);

  return (
    <div className="min-h-screen pt-24">
      <div className="mx-auto max-w-[1440px] px-5 pb-12 grid gap-8 xl:grid-cols-[280px_minmax(0,1fr)]">
        <aside className="xl:sticky xl:top-24 h-fit overflow-hidden rounded-[30px] border border-[var(--app-border)] bg-[var(--app-surface)] shadow-[0_24px_60px_rgba(15,23,42,0.06)] backdrop-blur-xl">
          <div className="border-b border-[var(--app-border)] px-7 py-7">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-2xl shadow-md">
                {getAvatarEmoji(user?.avatarIndex)}
              </div>
              <div className="min-w-0">
                <p className="text-lg font-semibold text-slate-950 truncate">{user?.name || 'Usuario'}</p>
                <p className="text-sm text-slate-500">{role || 'Sin rol'}</p>
              </div>
            </div>
          </div>

          <div ref={navRef} className="px-4 py-5">
            <nav className="space-y-3">
              <NavLink
                to={ROUTES.admin}
                end
                className={({ isActive }) => `block rounded-2xl px-4 py-3.5 text-[17px] font-medium transition ${
                  isActive
                    ? 'bg-[var(--app-accent-soft)] text-[var(--app-accent)] shadow-[inset_0_0_0_1px_rgba(0,113,227,0.08)]'
                    : 'text-slate-600 hover:bg-white/80 hover:text-slate-950'
                }`}
              >
                Dashboard
              </NavLink>
              {visibleMenuItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end
                  className={({ isActive }) => `block rounded-2xl px-4 py-3.5 text-[17px] font-medium transition ${
                    isActive
                      ? 'bg-[var(--app-accent-soft)] text-[var(--app-accent)] shadow-[inset_0_0_0_1px_rgba(0,113,227,0.08)]'
                      : 'text-slate-600 hover:bg-white/80 hover:text-slate-950'
                  }`}
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </div>
        </aside>

        <section ref={contentRef} className="min-w-0 space-y-6">
          <Outlet />
        </section>
      </div>
    </div>
  );
}
